import { useState } from 'react';
import { api } from '@shared/lib/api';
import type { Merchant } from '@shared/types';

interface Props {
  merchant: Merchant | null;
}

export default function StripeOnboardingBanner({ merchant }: Props) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!merchant) return null;

  const hasAccount = !!merchant.stripe_account_id;
  const isOnboarded = merchant.stripe_onboarded;

  if (isOnboarded) return null;

  const handleConnect = async () => {
    setLoading(true);
    setError('');
    try {
      if (!hasAccount) {
        await api.stripeCreateAccount(merchant.api_key);
      }
      const result = await api.stripeGetOnboardingUrl(merchant.api_key);
      window.location.href = result.onboarding_url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start Stripe onboarding');
      setLoading(false);
    }
  };

  return (
    <div
      className="rounded p-5 mb-6"
      style={{
        border: '1px solid var(--dash-border)',
        background: 'var(--dash-card)',
      }}
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h2
              className="font-mono text-[10px] uppercase tracking-widest"
              style={{ color: 'var(--dash-text-dim)' }}
            >
              Stripe Connect
            </h2>
            {hasAccount && (
              <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border text-amber-400 bg-amber-400/10 border-amber-400/20">
                Pending
              </span>
            )}
          </div>
          <p
            className="font-sans text-sm leading-relaxed max-w-xl"
            style={{ color: 'var(--dash-text-muted)' }}
          >
            {hasAccount
              ? 'Your Stripe account is being reviewed. Complete onboarding to start accepting card payments.'
              : 'Connect your Stripe account to start accepting card payments on your WooCommerce store. Settlement goes directly to your bank account.'}
          </p>
        </div>
      </div>

      {error && (
        <div className="border border-red-500/30 bg-red-500/10 rounded px-4 py-3 mt-4">
          <p className="font-sans text-sm text-red-300">{error}</p>
        </div>
      )}

      <button
        onClick={handleConnect}
        disabled={loading}
        className="mt-4 bg-blue-600 text-white font-mono text-sm font-medium px-5 py-2 rounded hover:bg-blue-500 transition-colors disabled:opacity-50 uppercase tracking-wider inline-flex items-center gap-2 group"
      >
        {loading ? (
          <>
            <span className="inline-block animate-spin text-sm">◌</span>
            Connecting...
          </>
        ) : (
          <>
            {hasAccount ? 'Continue Onboarding' : 'Connect with Stripe'}
            <span className="group-hover:translate-x-0.5 transition-transform text-sm">→</span>
          </>
        )}
      </button>
    </div>
  );
}
